import type { TimeSeriesChartDateRange } from '../types/TimeSeriesChartDateRange.types';
import { isValidDateRange } from './timeSeriesChartDateRange';

const TARGET_ID_PARAM = 'targetId';
const FROM_PARAM = 'from';
const TO_PARAM = 'to';

export type TimeSeriesChartUrlState =
  | {
      targetId: number;
      dates: TimeSeriesChartDateRange;
    }
  | {
      targetId: undefined;
      dates: undefined;
    };

export type TimeSeriesChartUrlStateInput = {
  targetId: number | undefined;
  dates: TimeSeriesChartDateRange | undefined;
};

function parsePositiveInteger(value: string | null): number | undefined {
  if (!value || !/^\d+$/.test(value)) {
    return undefined;
  }

  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    return undefined;
  }

  return parsed;
}

function parseDate(value: string | null): Date | undefined {
  if (!value || !/^\d+$/.test(value)) {
    return undefined;
  }

  const timestamp = Number(value);
  if (!Number.isSafeInteger(timestamp)) {
    return undefined;
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date;
}

export function parseTimeSeriesChartUrlState(searchParams: URLSearchParams): TimeSeriesChartUrlState {
  const targetId = parsePositiveInteger(searchParams.get(TARGET_ID_PARAM));
  const from = parseDate(searchParams.get(FROM_PARAM));
  const to = parseDate(searchParams.get(TO_PARAM));

  if (targetId === undefined || !from || !to || !isValidDateRange({ from, to })) {
    return { targetId: undefined, dates: undefined };
  }

  return { targetId, dates: { from, to } };
}

export function updateTimeSeriesChartSearchParams(
  current: URLSearchParams,
  state: TimeSeriesChartUrlStateInput
): URLSearchParams {
  const next = new URLSearchParams(current);

  if (state.targetId === undefined || state.dates === undefined || !isValidDateRange(state.dates)) {
    next.delete(TARGET_ID_PARAM);
    next.delete(FROM_PARAM);
    next.delete(TO_PARAM);
    return next;
  }

  next.set(TARGET_ID_PARAM, String(state.targetId));
  next.set(FROM_PARAM, String(state.dates.from.getTime()));
  next.set(TO_PARAM, String(state.dates.to.getTime()));

  return next;
}
